import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import "./InstallAppButton.css";

const InstallAppButton = () => {
  const { t } = useTranslation();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOSSafari, setIsIOSSafari] = useState(false);
  const [showIOSHint, setShowIOSHint] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || typeof navigator === "undefined") {
      return;
    }

    // Check if app is already installed
    const checkInstallation = () => {
      // Check for standalone display mode (Android/Desktop)
      const standaloneDisplay = window.matchMedia("(display-mode: standalone)").matches;
      
      // Check for iOS standalone mode
      const iosStandalone = 
        typeof navigator.standalone === "boolean" && navigator.standalone;
      
      const installed = standaloneDisplay || iosStandalone;
      setIsInstalled(installed);
      
      return installed;
    };

    // Detect iOS Safari
    const detectIOSSafari = () => {
      const ua = window.navigator.userAgent || "";
      const isIOSDevice = /iphone|ipad|ipod/i.test(ua);
      const isSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(ua);
      const isIOS = isIOSDevice && isSafari;
      setIsIOSSafari(isIOS);
      
      // Show iOS hint if not installed and on iOS Safari
      if (isIOS && !checkInstallation()) {
        setShowIOSHint(true);
      }
    };

    // Handle beforeinstallprompt event (Android Chrome, Desktop Chrome/Edge)
    const handleBeforeInstallPrompt = (event) => {
      // Prevent the default mini-infobar
      event.preventDefault();
      // Store the event for later use
      setDeferredPrompt(event);
    };

    // Handle successful installation
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
      setShowIOSHint(false);
    };

    // Handle display mode changes (when app is installed/uninstalled)
    const mediaQuery = window.matchMedia("(display-mode: standalone)");
    const handleDisplayModeChange = () => {
      checkInstallation();
    };

    // Initial checks
    checkInstallation();
    detectIOSSafari();

    // Add event listeners
    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);
    
    // Listen for display mode changes
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleDisplayModeChange);
    } else {
      // Fallback for older browsers
      mediaQuery.addListener(handleDisplayModeChange);
    }

    // Cleanup
    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.removeEventListener("appinstalled", handleAppInstalled);
      
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener("change", handleDisplayModeChange);
      } else {
        mediaQuery.removeListener(handleDisplayModeChange);
      }
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      return;
    }

    try {
      // Show the install prompt
      deferredPrompt.prompt();
      
      // Wait for the user to respond
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === "accepted") {
        // User accepted the install prompt
        setIsInstalled(true);
        setDeferredPrompt(null);
      } else {
        // User dismissed the install prompt
        setDeferredPrompt(null);
      }
    } catch (error) {
      console.error("Error showing install prompt:", error);
      setDeferredPrompt(null);
    }
  };

  // Don't show anything if app is already installed
  if (isInstalled) {
    return null;
  }

  // Show install button for Android/Desktop Chrome/Edge
  if (deferredPrompt && !isIOSSafari) {
    return (
      <button 
        className="install-app-button" 
        onClick={handleInstallClick}
        aria-label={t("installApp.button")}
      >
        <svg 
          width="20" 
          height="20" 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke="currentColor" 
          strokeWidth="2"
          style={{ marginRight: "8px" }}
        >
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="7 10 12 15 17 10" />
          <line x1="12" y1="15" x2="12" y2="3" />
        </svg>
        {t("installApp.button")}
      </button>
    );
  }

  // Show iOS hint message
  if (isIOSSafari && showIOSHint) {
    return (
      <div className="install-ios-hint">
        <svg 
          width="16" 
          height="16" 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke="currentColor" 
          strokeWidth="2"
          style={{ marginRight: "6px", flexShrink: 0 }}
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="16" x2="12" y2="12" />
          <line x1="12" y1="8" x2="12.01" y2="8" />
        </svg>
        <span>{t("installApp.iosHint")}</span>
      </div>
    );
  }

  // Fallback: show install button for all browsers
  // When clicked, guide the user to install manually
  const handleFallbackInstall = () => {
    const ua = navigator.userAgent || "";
    const isChrome = /chrome/i.test(ua) && !/edge/i.test(ua);
    const isEdge = /edg/i.test(ua);
    
    if (isChrome || isEdge) {
      alert(t("installApp.chromeHint") || "To install: Click the install icon (⊕) in the browser address bar, or go to Menu → Install App");
    } else {
      alert(t("installApp.genericHint") || "To install: Open this site in Chrome or Edge, then click the install icon in the address bar");
    }
  };

  return (
    <button 
      className="install-app-button" 
      onClick={handleFallbackInstall}
      aria-label={t("installApp.button")}
    >
      <svg 
        width="20" 
        height="20" 
        viewBox="0 0 24 24" 
        fill="none" 
        stroke="currentColor" 
        strokeWidth="2"
        style={{ marginRight: "8px" }}
      >
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <polyline points="7 10 12 15 17 10" />
        <line x1="12" y1="15" x2="12" y2="3" />
      </svg>
      {t("installApp.button")}
    </button>
  );
};

export default InstallAppButton;
